import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import APISERVICE from '../../services/api-service';

export default function LiveStreamSnapshot({ channelName, fallbackImage, onClick, width = 280, height = 157 }) {
  const [showClickOverlay, setShowClickOverlay] = useState(false);
  const [snapshotUrl, setSnapshotUrl] = useState(fallbackImage);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    const fetchSnapshot = async () => {
      if (!channelName) return;

      try {
        const response = await APISERVICE.get(`/snapshot/livestream/${channelName}/snapshot`);

        let newSnapshotUrl = null;

        if (response?.result?.url) {
          newSnapshotUrl = response.result.url;
        } else if (response?.data?.result?.url) {
          newSnapshotUrl = response.data.result.url;
        } else if (response?.url) {
          newSnapshotUrl = response.url;
        }

        if (newSnapshotUrl && !newSnapshotUrl.includes('placehold.co')) {
          // Append timestamp to bust browser cache so snapshots update live
          const separator = newSnapshotUrl.includes('?') ? '&' : '?';
          setSnapshotUrl(`${newSnapshotUrl}${separator}t=${Date.now()}`);
          setIsLoading(false);
        }
      } catch (error) {
        if (error.response?.status === 403) {
          setSubscriptionRequired(true);
          setIsLoading(false);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return;
        }

        // Session gone (expired token): stop polling, the HTTP layer is
        // already signing the tab out.
        const message = error.response?.data?.message;
        if (error.response?.status === 401 || (typeof message === "string" && message.startsWith("JWT"))) {
          setIsLoading(false);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return;
        }

        if (error.response?.status === 404) {
          // No snapshot yet — show playlist image as fallback, keep polling
          if (fallbackImage) {
            setSnapshotUrl(fallbackImage);
          }
          setIsLoading(false);
          return;
        }
      }
    };

    fetchSnapshot();
    intervalRef.current = setInterval(fetchSnapshot, 30000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [channelName, fallbackImage]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: `${width}px`,
        height: `${height}px`,
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: subscriptionRequired ? 'default' : 'pointer',
        backgroundColor: '#000',
        '&:hover': subscriptionRequired ? {} : {
          transform: 'scale(1.05)',
          transition: 'all 0.3s ease',
        }
      }}
      onClick={subscriptionRequired ? undefined : onClick}
      onMouseEnter={() => !subscriptionRequired && setShowClickOverlay(true)}
      onMouseLeave={() => setShowClickOverlay(false)}
    >
      {/* Snapshot/Fallback image */}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <img
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '8px',
            transition: 'opacity 0.3s ease-in-out',
            opacity: 1,
            filter: subscriptionRequired ? 'blur(12px)' : 'none',
          }}
          src={snapshotUrl}
          alt=""
          onLoad={(e) => {
            e.target.style.opacity = '1';
          }}
        />
      </Box>

      {/* Subscription required overlay */}
      {subscriptionRequired && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(0,0,0,0.5)',
          }}
        >
          <LockIcon sx={{ color: '#fff', fontSize: 28, mb: 0.5 }} />
          <Typography
            sx={{
              color: '#fff',
              fontSize: '12px',
              fontWeight: 600,
              textAlign: 'center',
              letterSpacing: '0.3px',
            }}
          >
            Subscribe to Preview
          </Typography>
        </Box>
      )}

      {/* Click to watch overlay */}
      {!subscriptionRequired && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)',
            padding: '20px 10px 10px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            opacity: showClickOverlay ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}
        >
          <Typography
            sx={{
              color: 'white',
              fontSize: '13px',
              fontWeight: 'bold',
              textAlign: 'center',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            ▶ Watch Live
          </Typography>
        </Box>
      )}
    </Box>
  );
}